// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

/**
 * The logged-in user's own profile.
 *
 * Deliberately not the `employees` slice: that one is the Team page's full
 * directory, fetched from the admin-gated /v1/users, which 403s for every
 * non-admin. /v1/users/me is authenticated-only and returns one record.
 */
export const fetchMyProfile = createAsyncThunk(
  'currentUser/fetchMyProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/v1/users/me')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch own profile')
    }
  }
)

/**
 * The app roles FMS grants the caller on top of their token's Entra roles
 * (a User Access 'HR' entry → Salary Reviews + Team). Kept apart from
 * `profile`: /v1/users/me resolves the caller through HRMS, which the route
 * guards must never wait on; /v1/users/me/roles reads the FMS database only.
 *
 * Fetched once per session by Router; the condition dedupes a second dispatch
 * while one is in flight or after a success, and allows one after a failure.
 */
export const GRANTED_ROLES_TIMEOUT_MS = 15000

export const fetchMyGrantedRoles = createAsyncThunk(
  'currentUser/fetchMyGrantedRoles',
  async (_, { rejectWithValue }) => {
    try {
      // Bounded: the guards wait on this for anyone the token would refuse.
      const response = await axios.get('/v1/users/me/roles', { timeout: GRANTED_ROLES_TIMEOUT_MS })
      const roles = response.data?.grantedRoles
      return Array.isArray(roles) ? roles : []
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch granted roles')
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState().currentUser
      if (!state) return true
      if (state.grantedRolesLoading) return false
      // Once per session on success; a failed lookup may be retried.
      return state.grantedRoles === null || state.grantedRolesError
    }
  }
)

export const currentUserSlice = createSlice({
  name: 'currentUser',
  initialState: {
    profile: null,
    loading: false,
    error: null,
    // null until /v1/users/me/roles settles; [] after a failure (grants none)
    grantedRoles: null,
    grantedRolesLoading: false,
    grantedRolesError: false
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchMyProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMyProfile.fulfilled, (state, action) => {
        state.loading = false
        // The API answers 200 {} when it cannot resolve the caller, so an
        // empty object is a settled outcome, not a retry signal.
        state.profile = action.payload || {}
        state.error = null
      })
      .addCase(fetchMyProfile.rejected, (state, action) => {
        state.loading = false
        // Settle to {} rather than back to null: the fetch effect keys off
        // `profile === null`, so leaving it null would retry forever.
        state.profile = {}
        state.error = action.payload
      })
      .addCase(fetchMyGrantedRoles.pending, (state) => {
        state.grantedRolesLoading = true
      })
      .addCase(fetchMyGrantedRoles.fulfilled, (state, action) => {
        state.grantedRolesLoading = false
        state.grantedRolesError = false
        state.grantedRoles = action.payload
      })
      .addCase(fetchMyGrantedRoles.rejected, (state) => {
        state.grantedRolesLoading = false
        state.grantedRolesError = true
        // Settle to [] (never back to null): the guards wait on null, and a
        // failed lookup grants nothing.
        state.grantedRoles = []
      })
  }
})

export default currentUserSlice.reducer
